/* ============================================================
   Optimate para Shopify.
   - El logotipo gigante baja contigo y se encoge hasta la barra (portada y producto).
   - La frase se llena de tinta con el scroll.
   - El mapa de envíos anima sus rutas; el 3D se descarga solo al acercarse.
   - La bolsa lateral usa el carrito real de Shopify (/cart/add.js, /cart.js, /cart/change.js).
   - Validación de correos y aviso de cookies de Shopify.
   ============================================================ */
(() => {
  const root = document.documentElement;
  const body = document.body;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp01 = v => Math.min(1, Math.max(0, v));
  const EASE_OUT = 'cubic-bezier(.23,1,.32,1)';

  // Medida de la pantalla que de verdad se ve. Dentro de visores (apps, WebViews de iOS) innerWidth
  // puede reportar el ancho de escritorio: se usa el ancho real del layout y un alto acotado a la pantalla.
  function viewport() {
    const w = Math.min(root.clientWidth || Infinity, innerWidth || Infinity);
    const h = Math.min(root.clientHeight || Infinity, innerHeight || Infinity, (window.visualViewport && visualViewport.height * 1.2) || Infinity, screen.height || Infinity);
    return { w: isFinite(w) ? w : 390, h: isFinite(h) ? h : 800 };
  }

  /* ============================================================
     1. Logotipo gigante
     ============================================================ */
  const mark = document.getElementById('mark');
  const markText = document.getElementById('markText');
  const space = document.getElementById('markSpace');
  const bar = document.querySelector('.bar');

  const BAR = 56;           // alto de la barra
  const LOGO_H = 20;        // alto final del logotipo, en px
  const TOP_START = 0.06;   // arranque del logotipo, como fracción del alto de pantalla

  let bigH = 0, padX = 0, travel = 1, lastY = -1, lastW = 0, lastOut = null;
  const cssScroll = !!mark && CSS.supports('animation-timeline: scroll()') && !reduceMotion;
  if (cssScroll) root.classList.add('has-scroll-timeline');

  // el logotipo ocupa todo el ancho: se ajusta el tamaño de fuente al ancho disponible
  function fit() {
    if (!mark) return;
    const vp = viewport();
    padX = parseFloat(getComputedStyle(mark).paddingLeft);
    const avail = vp.w - padX * 2;
    markText.style.fontSize = '100px';
    const w100 = markText.getBoundingClientRect().width || 1;
    let size = Math.floor(avail / w100 * 100 * 100) / 100;
    markText.style.fontSize = size + 'px';
    const w1 = markText.getBoundingClientRect().width;
    if (w1 > avail) { size = Math.floor(size * avail / w1 * 100) / 100; markText.style.fontSize = size + 'px'; }
    bigH = markText.getBoundingClientRect().height;
    const startY = vp.h * TOP_START;
    const spaceH = Math.round(startY + bigH + vp.h * .06);
    space.style.height = spaceH + 'px';
    root.style.setProperty('--mark-space', spaceH + 'px');
    const s = LOGO_H / bigH;
    mark.style.setProperty('--dock-s', s.toFixed(4));
    mark.style.setProperty('--dock-x', (padX * (1 - s)).toFixed(2) + 'px');
    // la transición termina antes de que el texto de la portada pase bajo el logo a medio encoger
    const cover = document.getElementById('cover');
    const coverEnd = cover ? cover.getBoundingClientRect().bottom + scrollY : vp.h;
    travel = Math.max(200, Math.min(vp.h * .9, coverEnd * .75));
    root.style.setProperty('--mark-top', startY.toFixed(1) + 'px');
    root.style.setProperty('--mark-travel', travel.toFixed(0) + 'px');
    lastY = -1; lastW = vp.w;
  }

  const ease = t => 1 - Math.pow(1 - t, 2.2);
  const startTop = () => viewport().h * TOP_START;
  const topEnd = (BAR - LOGO_H) / 2;
  const dockedTransform = () => {
    const s = LOGO_H / bigH;
    return `translate3d(${(padX * (1 - s)).toFixed(2)}px, ${topEnd}px, 0) scale(${s.toFixed(4)})`;
  };

  function place() {
    if (!mark) return;
    const y = scrollY;
    if (y === lastY) return;
    lastY = y;
    if (!bigH) return;
    if (reduceMotion) {
      const out = y > startTop() + bigH;
      mark.style.transform = out ? dockedTransform() : `translate3d(0, ${(startTop() - y).toFixed(2)}px, 0)`;
      if (out !== lastOut) {
        if (lastOut !== null && out) mark.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease' });
        lastOut = out;
      }
      mark.classList.toggle('is-docked', out);
      bar.classList.toggle('is-solid', out);
      return;
    }
    const p = ease(clamp01(y / travel));
    if (!cssScroll) {
      const scale = 1 + (LOGO_H / bigH - 1) * p;
      const top = startTop() + (topEnd - startTop()) * p;
      const tx = padX * (1 - scale);
      mark.style.transform = `translate3d(${tx.toFixed(2)}px, ${top.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
    }
    mark.classList.toggle('is-docked', p > .98);
    bar.classList.toggle('is-solid', p > .6);
  }

  /* ============================================================
     2. La frase se llena de tinta y ocupa todo el ancho
     ============================================================ */
  const statement = document.getElementById('statement');
  const cssFill = !!statement && CSS.supports('animation-timeline: view()') && !reduceMotion;
  if (cssFill) root.classList.add('has-view-timeline');
  let lastFill = -1;
  function fillStatement() {
    if (!statement || reduceMotion || cssFill) return;
    const r = statement.getBoundingClientRect();
    const vh = viewport().h;
    const p = clamp01((vh - r.top) / (vh * .5 + r.height));
    const f = Math.round(p * 1000) / 10;
    if (f === lastFill) return;
    lastFill = f;
    statement.style.setProperty('--fill', f + '%');
  }
  // se mide la frase real (el comerciante puede cambiarla en el editor)
  function fitStatement() {
    if (!statement) return;
    const vw = viewport().w;
    if (vw <= 600) { statement.style.fontSize = ''; return; }   // en celular se parte en líneas desde CSS
    const pad = parseFloat(getComputedStyle(statement.parentElement).paddingLeft) || 0;
    const avail = vw - pad * 2;
    statement.style.fontSize = '100px';
    const w = (statement.getBoundingClientRect().width - 8) || 1047;   // sin el padding de .04em por lado
    statement.style.fontSize = (Math.floor(avail / w * 100 * 100) / 100) + 'px';
  }

  let queued = false;
  function request() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fillStatement(); place(); });
  }
  if (mark) {
    bigH = markText.getBoundingClientRect().height;
    travel = Math.max(200, viewport().h * .6);   // respaldo hasta que fit() mida la portada
  }
  if (mark || statement) {
    request();
    addEventListener('scroll', request, { passive: true });
    document.fonts.load('600 100px Jost').then(() => { fit(); request(); }).catch(() => { fit(); request(); });
    document.fonts.load('400 100px "Cormorant Garamond"').then(fitStatement).catch(fitStatement);
    document.fonts.addEventListener('loadingdone', () => { fit(); fitStatement(); lastY = -1; request(); });
    const onResize = () => { if (viewport().w !== lastW) { fit(); fitStatement(); lastY = -1; request(); } };
    addEventListener('resize', onResize);
    if (window.ResizeObserver) new ResizeObserver(onResize).observe(root);
    addEventListener('orientationchange', () => { fit(); lastY = -1; request(); });
  }
  // en el editor de temas las secciones se recargan sin recargar la página
  document.addEventListener('shopify:section:load', () => { fit(); fitStatement(); lastY = -1; request(); });

  /* ---------- edición limitada: el número cuenta de 001 a 100 cuando entra en pantalla ---------- */
  function countUp() {
    const el = document.getElementById('editionCount');
    if (!el) return;
    const to = Number(el.dataset.to) || 100;
    const fmt = n => String(n).padStart(String(to).length, '0');
    el.textContent = fmt(to);
    if (reduceMotion || !window.IntersectionObserver) return;      // sin animación: el total desde el inicio
    el.textContent = fmt(1);
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      // rápido al principio y se asienta al llegar al total
      const t0 = performance.now(), D = 1600, out = t => 1 - Math.pow(1 - t, 3);
      const tick = now => {
        const t = Math.min(1, (now - t0) / D);
        el.textContent = fmt(Math.max(1, Math.round(1 + (to - 1) * out(t))));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: .6 });
    io.observe(el);
  }
  countUp();
  document.addEventListener('shopify:section:load', countUp);

  /* ============================================================
     3. Mapa de envíos
     ============================================================ */
  const loadScript = src => new Promise((ok, err) => {
    const s = document.createElement('script'); s.src = src; s.async = true; s.onload = ok; s.onerror = err;
    document.head.appendChild(s);
  });

  function initMap() {
    const el = document.getElementById('shipMap');
    if (!el || el.dataset.ready) return;
    el.dataset.ready = '1';
    const svg = el.querySelector('.map__fallback');
    if (svg) {
      const NS = 'http://www.w3.org/2000/svg';
      const arcs = [...svg.querySelectorAll('.arc')];
      const pins = [...svg.querySelectorAll('.pin')];
      pins.forEach((pin, i) => pin.style.setProperty('--d', `${i === 0 ? 0.2 : (0.2 + (i - 1) * 0.09 + 1.2).toFixed(2)}s`));
      arcs.forEach((arc, i) => {
        arc.setAttribute('pathLength', '1');
        arc.id = arc.id || `route-${i}`;
        arc.style.animationDelay = `${0.2 + i * 0.09}s`;
        if (reduceMotion) return;
        const dot = document.createElementNS(NS, 'circle');
        dot.setAttribute('r', '1.8'); dot.setAttribute('class', 'arc__pulse');
        dot.setAttribute('visibility', 'hidden');
        const begin = `${(2 + i * 0.09 + Math.random()).toFixed(2)}s`;
        const show = document.createElementNS(NS, 'set');
        show.setAttribute('attributeName', 'visibility'); show.setAttribute('to', 'visible'); show.setAttribute('begin', begin);
        dot.appendChild(show);
        const move = document.createElementNS(NS, 'animateMotion');
        move.setAttribute('dur', `${(2.2 + arc.getTotalLength() / 90).toFixed(2)}s`);
        move.setAttribute('begin', begin);
        move.setAttribute('repeatCount', 'indefinite');
        const mpath = document.createElementNS(NS, 'mpath');
        mpath.setAttribute('href', `#${arc.id}`);
        move.appendChild(mpath); dot.appendChild(move);
        svg.appendChild(dot);
      });
      if (reduceMotion) svg.classList.add('is-animated');
      else {
        svg.pauseAnimations();
        new IntersectionObserver(([e], o) => {
          if (e.intersectionRatio < .35) return;
          o.disconnect();
          svg.dataset.t0 = String(performance.now());
          svg.setCurrentTime(0); svg.unpauseAnimations();
          svg.classList.add('is-animated');
        }, { threshold: [.35] }).observe(svg);
      }
    }
    // three.js recortado (~500 KB): solo cuando el mapa se acerca a la pantalla
    if (el.hasAttribute('data-no-3d') || !body.dataset.threeSrc) return;
    const io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      const ready = window.ShipMap3D ? Promise.resolve() : loadScript(body.dataset.threeSrc).then(() => loadScript(body.dataset.mapSrc));
      ready.then(() => {
        if (!(window.ShipMap3D && ShipMap3D.supported)) return;
        try { ShipMap3D.mount(el); } catch (e) { console.warn('ShipMap3D', e); }
      }).catch(() => {});
    }, { rootMargin: '1200px 0px' });
    io.observe(el);
  }
  initMap();
  document.addEventListener('shopify:section:load', initMap);

  /* ============================================================
     4. Bolsa con el carrito de Shopify
     ============================================================ */
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CURRENCY = body.dataset.currency || 'MXN';
  const FREE_SHIP = Number(body.dataset.freeShipping) || 0;   // en centavos
  const money = cents => {
    const n = cents / 100;
    const s = n.toLocaleString('es-MX', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 });
    return `$${s} ${CURRENCY}`;
  };
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const imgAt = (src, w) => {
    if (!src) return '';
    try { const u = new URL(src, location.href); u.searchParams.set('width', w); return u.href; } catch (e) { return src; }
  };

  const drawer = document.getElementById('drawer');
  const bagBtn = document.getElementById('bagBtn');
  const bagCount = document.getElementById('bagCount');
  const bagItems = document.getElementById('bagItems');
  const bagTotal = document.getElementById('bagTotal');
  const shipNote = document.getElementById('shipNote');
  const checkout = document.getElementById('checkout');
  const checkoutMsg = document.getElementById('checkoutMsg');
  const headCount = document.getElementById('bagHeadCount');
  const shipBar = document.getElementById('shipBar');
  const shipMeter = document.getElementById('shipMeter');
  const checkoutTotal = document.getElementById('checkoutTotal');
  const D = drawer ? drawer.dataset : {};
  let cart = null, pendingBump = false, wasFree = false, busy = false;

  const reveal = el => el && el.animate(
    reduceMotion ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }],
    { duration: 200, easing: EASE_OUT });

  function bumpCount() {
    if (!bagCount) return;
    bagCount.getAnimations().forEach(a => a.cancel());
    if (reduceMotion) bagCount.animate([{ color: '#2c3f6b' }, { color: 'inherit' }], { duration: 200, easing: 'ease' });
    else bagCount.animate([{ transform: 'scale(1.2)' }, { transform: 'scale(1)' }], { duration: 240, easing: EASE_OUT });
  }

  const say = (text, kind) => {
    if (!checkoutMsg) return;
    checkoutMsg.textContent = text || '';
    checkoutMsg.classList.toggle('is-error', kind === 'error');
    if (text) reveal(checkoutMsg);
  };

  async function api(path, data) {
    const res = await fetch(ROOT + path, data instanceof FormData
      ? { method: 'POST', body: data, headers: { Accept: 'application/json' } }
      : data ? { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
        : { headers: { Accept: 'application/json' } });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.description || json.message || 'Error ' + res.status);
      err.status = res.status;
      throw err;
    }
    return json;
  }

  // la pieza del drop usa su foto recortada sobre el gris de estudio; otros productos, su foto
  const lineImg = item => (D.cutout && String(item.product_id) === String(D.cutoutProduct))
    ? `<img src="${esc(D.cutout)}" alt="" width="160" height="120" loading="lazy" />`
    : (item.image ? `<img class="is-photo" src="${esc(imgAt(item.image, 240))}" alt="" width="104" height="104" loading="lazy" />` : '');
  const unitText = item => item.quantity > 1 ? `${item.quantity} × ${money(item.final_price)}` : '';

  function lineHTML(item) {
    const isDrop = String(item.product_id) === String(D.cutoutProduct);
    const sub = isDrop && D.lineSub ? D.lineSub
      : [item.variant_title, item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.name].filter(Boolean).join(' · ');
    const name = esc(item.product_title);
    return `<div class="line" data-key="${esc(item.key)}">
        <a class="line__art" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${lineImg(item)}</a>
        <div class="line__info">
          <div class="line__top"><p class="line__name">${name}</p><span class="line__price">${money(item.final_line_price)}</span></div>
          ${sub ? `<p class="line__sub">${esc(sub)}</p>` : ''}
          <p class="line__unit">${unitText(item)}</p>
          <div class="line__row">
            <div class="qty" role="group" aria-label="Cantidad de ${name}">
              <button type="button" data-d="-1" aria-label="Quitar una">−</button>
              <span class="line__count" aria-live="polite">${item.quantity}</span>
              <button type="button" data-d="1" aria-label="Agregar una">+</button>
            </div>
            <button type="button" class="line__remove" data-remove>Quitar</button>
          </div>
        </div>
      </div>`;
  }

  const emptyHTML = () => `<div class="drawer__empty">
      ${D.cutout ? `<img src="${esc(D.cutout)}" width="160" height="120" alt="" />` : ''}
      <p class="drawer__empty-title">Tu bolsa está vacía</p>
      <p>Cien piezas por edición. La tuya te espera.</p>
      <a class="btn btn--ghost btn--sm" href="${esc(D.emptyUrl || '#pieza')}" data-close>Ver la gorra</a>
    </div>`;

  function renderBag(next) {
    if (!next || !drawer) return;
    const prev = cart;
    cart = next;
    const qty = cart.item_count;
    if (bagCount) {
      const changed = String(qty) !== bagCount.textContent;
      bagCount.textContent = qty;
      if (changed && prev) { if (drawer.classList.contains('is-open')) pendingBump = true; else bumpCount(); }
    }
    if (bagBtn) bagBtn.setAttribute('aria-label', qty ? `Abrir bolsa, ${qty} ${qty === 1 ? 'pieza' : 'piezas'}` : 'Abrir bolsa, vacía');
    bagTotal.textContent = money(cart.total_price);
    if (headCount) headCount.textContent = qty ? qty : '';
    if (checkoutTotal) checkoutTotal.textContent = qty ? `· ${money(cart.total_price)}` : '';
    drawer.classList.toggle('is-empty', !qty);

    // las líneas que ya existen se actualizan en su lugar para no perder el foco de los botones
    const keys = cart.items.map(i => i.key);
    bagItems.querySelectorAll('.line').forEach(l => {
      if (keys.includes(l.dataset.key)) return;
      l.animate([{ opacity: 1 }, { opacity: 0, transform: reduceMotion ? 'none' : 'translateY(-4px)' }], { duration: 150, easing: EASE_OUT })
        .finished.then(() => l.remove(), () => l.remove());
    });
    let before = null;
    [...cart.items].reverse().forEach(item => {
      let el = bagItems.querySelector(`.line[data-key="${CSS.escape(item.key)}"]`);
      if (el) {
        el.querySelector('.line__count').textContent = item.quantity;
        el.querySelector('.line__price').textContent = money(item.final_line_price);
        el.querySelector('.line__unit').textContent = unitText(item);
      } else {
        const t = document.createElement('template');
        t.innerHTML = lineHTML(item).trim();
        el = t.content.firstElementChild;
        bagItems.insertBefore(el, before);
      }
      before = el;
    });
    const empty = bagItems.querySelector('.drawer__empty');
    if (qty && empty) empty.remove();
    if (!qty && !empty) {
      setTimeout(() => { if (!cart.item_count && !bagItems.querySelector('.drawer__empty')) bagItems.insertAdjacentHTML('beforeend', emptyHTML()); }, 160);
    }

    if (shipNote) {
      const free = FREE_SHIP > 0 && cart.total_price >= FREE_SHIP;
      shipNote.textContent = !qty || !FREE_SHIP ? '' : free
        ? 'Tu envío es gratis a todo México.'
        : `Te faltan ${money(FREE_SHIP - cart.total_price)} para envío gratis.`;
      if (shipBar) shipBar.hidden = !qty || !FREE_SHIP;
      if (shipMeter) shipMeter.style.setProperty('--p', FREE_SHIP ? Math.min(100, cart.total_price / FREE_SHIP * 100).toFixed(1) + '%' : '0%');
      shipNote.classList.toggle('is-free', free && qty > 0);
      if (free && !wasFree && qty && prev) reveal(shipNote);
      wasFree = free && qty > 0;
    }
    if (checkout) checkout.disabled = !qty;
  }

  const refresh = () => api('cart.js').then(renderBag).catch(() => {});

  // el resto de la página queda inerte mientras la bolsa está abierta: el foco no se escapa del diálogo
  const background = () => document.querySelectorAll('main, .bar, .mark, .shopify-section-group-header-group, .shopify-section-group-footer-group, .foot, .buybar, .skip');
  let lastTrigger = null;
  function openDrawer(open, trigger) {
    if (!drawer || open === drawer.classList.contains('is-open')) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    drawer.inert = !open;
    background().forEach(n => { n.inert = open; });
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      lastTrigger = trigger || document.activeElement;
      document.getElementById('drawerClose').focus();
    } else {
      if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
      say('');
      if (pendingBump) { pendingBump = false; bumpCount(); }
    }
  }

  if (drawer) {
    drawer.inert = true;
    // agregar a la bolsa: el formulario funciona sin JS (va a /cart); con JS se agrega por AJAX
    document.addEventListener('submit', async e => {
      const form = e.target.closest('form[data-add-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      if (busy) return;
      busy = true;
      if (btn) btn.setAttribute('aria-busy', 'true');
      try {
        await api('cart/add.js', new FormData(form));
        await refresh();
        openDrawer(true, btn);
      } catch (err) {
        await refresh();
        openDrawer(true, btn);
        // 422: ya no hay más piezas de las que tienes en la bolsa
        say(err.status === 422 ? 'Ya tienes en tu bolsa todas las piezas disponibles.' : 'No pudimos agregar la pieza. Intenta de nuevo.', 'error');
      } finally {
        busy = false;
        if (btn) btn.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('click', async e => {
      const d = e.target.closest('#drawer [data-d], #drawer [data-remove]');
      if (d) {
        const line = d.closest('.line');
        const item = cart && cart.items.find(i => i.key === line.dataset.key);
        if (!item || busy) return;
        busy = true;
        const quantity = d.hasAttribute('data-remove') ? 0 : Math.max(0, item.quantity + Number(d.dataset.d));
        try {
          renderBag(await api('cart/change.js', { id: item.key, quantity }));
          say('');
          if (!quantity) document.getElementById('drawerClose').focus();
        } catch (err) {
          await refresh();
          say(err.status === 422 ? 'No hay más piezas disponibles.' : 'No pudimos actualizar tu bolsa.', 'error');
        } finally { busy = false; }
        return;
      }
      if (e.target.closest('#drawer [data-close]')) { openDrawer(false); return; }
      if (bagBtn && e.target.closest('#bagBtn')) {
        e.preventDefault();
        openDrawer(true, bagBtn);
        refresh();
      }
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') openDrawer(false);
      // el enlace de la bolsa se comporta como botón también con la barra espaciadora
      if (e.key === ' ' && e.target === bagBtn) { e.preventDefault(); openDrawer(true, bagBtn); refresh(); }
    });
    // al volver con "atrás" el navegador puede mostrar una copia vieja de la página
    addEventListener('pageshow', e => { if (e.persisted) refresh(); });
    if (bagCount && bagCount.textContent.trim() !== '0') refresh();
    else cart = { item_count: 0, items: [], total_price: 0 };
  }

  /* ---------- barra de compra: aparece cuando los botones de compra ya no están a la vista ---------- */
  const buybar = document.getElementById('buybar');
  const ctas = [...document.querySelectorAll('.cover__buy .btn, #addBtn')];
  if (buybar && ctas.length) {
    const ctaState = new Map();
    const st = c => ctaState.get(c) || { visible: true, above: false };

    // deslizar hacia abajo para quitarla: sigue al dedo, se desvanece y sale suave; si no llega, regresa con resorte
    let buybarDismissed = false;
    (function swipeToDismiss(bar) {
      const EASE = 'cubic-bezier(.32,.72,0,1)';
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      let id = null, y0 = 0, dy = 0, h = 0, dragging = false, samples = [], eatClick = false;
      const set = (y, o, tr) => { bar.style.transition = tr; bar.style.transform = y === null ? '' : `translate3d(0,${y}px,0)`; bar.style.opacity = o === null ? '' : String(o); };
      bar.addEventListener('pointerdown', e => {
        if (!bar.classList.contains('is-on') || e.button > 0) return;
        id = e.pointerId; y0 = e.clientY; dy = 0; dragging = false; samples = [[e.timeStamp, 0]];
        h = bar.getBoundingClientRect().height;
      });
      bar.addEventListener('pointermove', e => {
        if (e.pointerId !== id) return;
        const d = e.clientY - y0;
        if (!dragging) {
          if (Math.abs(d) < 6) return;
          dragging = true; try { bar.setPointerCapture(id); } catch (_) {}
        }
        // hacia abajo sigue al dedo; hacia arriba se resiste
        dy = d > 0 ? d : -Math.sqrt(-d) * 2;
        set(dy, Math.max(0, 1 - Math.max(0, dy) / (h * 3.2)), 'none');
        samples.push([e.timeStamp, dy]); if (samples.length > 6) samples.shift();
      });
      const end = e => {
        if (e.pointerId !== id) return;
        id = null;
        if (!dragging) return;
        dragging = false; eatClick = true; setTimeout(() => { eatClick = false; }, 350);
        const [ta, ya] = samples[0], [tb, yb] = samples[samples.length - 1];
        const v = (yb - ya) / Math.max(1, tb - ta);           // px/ms, positivo hacia abajo
        if (e.type !== 'pointercancel' && (dy > h * 0.55 || v > 0.45)) {
          const off = h + (innerHeight - bar.getBoundingClientRect().bottom) + 24;
          set(reduce ? 0 : off, 0, reduce ? 'opacity 150ms ease' : `transform ${v > 0.9 ? 200 : 280}ms ${EASE},opacity 240ms ease`);
          setTimeout(() => {
            buybarDismissed = true;
            bar.classList.remove('is-on'); bar.setAttribute('aria-hidden', 'true'); bar.inert = true;
            set(null, null, 'none');
            requestAnimationFrame(() => { bar.style.transition = ''; });
          }, reduce ? 160 : 290);
        } else {
          set(null, null, `transform 420ms ${EASE},opacity 300ms ease`);
          setTimeout(() => { if (!dragging) bar.style.transition = ''; }, 440);
        }
      };
      bar.addEventListener('pointerup', end);
      bar.addEventListener('pointercancel', end);
      // un arrastre no cuenta como toque en "Agregar a la bolsa"
      bar.addEventListener('click', e => { if (eatClick) { e.preventDefault(); e.stopPropagation(); eatClick = false; } }, true);
    })(buybar);
    const ctaIO = new IntersectionObserver(entries => {
      entries.forEach(en => ctaState.set(en.target, { visible: en.isIntersecting, above: en.boundingClientRect.top < 0 }));
      // sale en cuanto el botón de la portada se va hacia arriba y se queda; solo se va si vuelves arriba.
      // Si la quitaron deslizándola, vuelve a salir hasta después de regresar al botón de la portada.
      if (st(ctas[0]).visible) buybarDismissed = false;
      const on = !st(ctas[0]).visible && st(ctas[0]).above && !buybarDismissed;
      buybar.classList.toggle('is-on', on);
      buybar.setAttribute('aria-hidden', String(!on));
      buybar.inert = !on;
    });
    ctas.forEach(c => ctaIO.observe(c));
    buybar.inert = true;
  }

  /* ============================================================
     5. Formularios: el envío lo hace Shopify; aquí solo se valida antes
     ============================================================ */
  const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i;
  const TYPOS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gamil.com': 'gmail.com', 'gmail.con': 'gmail.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.co': 'hotmail.com', 'hotmail.con': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outlook.co': 'outlook.com', 'yahoo.co': 'yahoo.com', 'yaho.com': 'yahoo.com', 'icloud.co': 'icloud.com',
  };
  function checkEmail(raw) {
    const v = raw.trim().toLowerCase();
    if (!v) return { error: 'Escribe tu correo.' };
    if (v.length > 254 || !EMAIL.test(v)) return { error: 'Revisa tu correo. Debe verse como nombre@correo.com.' };
    const [user, domain] = v.split('@');
    if (TYPOS[domain]) return { error: `¿Quisiste decir ${user}@${TYPOS[domain]}?`, fix: `${user}@${TYPOS[domain]}` };
    return { value: v };
  }
  const RULES = {
    name: v => v.trim().length < 2 ? 'Escribe tu nombre.' : '',
    email: v => checkEmail(v).error || '',
    order: v => v.trim() && !/^#?[A-Za-z0-9-]{3,20}$/.test(v.trim()) ? 'El número de pedido solo lleva letras, números y guiones.' : '',
    message: v => v.trim().length < 10 ? 'Cuéntanos un poco más (mínimo 10 caracteres).' : v.length > 2000 ? 'El mensaje es muy largo (máximo 2,000 caracteres).' : '',
  };

  /* Avisos del drop (pie de página): un solo correo, mensaje debajo */
  const news = document.getElementById('newsForm');
  if (news) {
    const input = news.querySelector('input[type="email"]');
    const msg = document.getElementById('newsMsg');
    let tried = false;
    const show = (text, kind) => {
      msg.textContent = text;
      msg.classList.toggle('is-error', kind === 'error');
      msg.classList.toggle('is-ok', kind === 'ok');
      reveal(msg);
    };
    input.addEventListener('blur', () => {
      if (!tried || !input.value) return;
      const r = checkEmail(input.value);
      input.setAttribute('aria-invalid', String(!!r.error));
      if (r.error) show(r.error, 'error'); else show('', '');
    });
    news.addEventListener('submit', e => {
      tried = true;
      const r = checkEmail(input.value);
      input.setAttribute('aria-invalid', String(!!r.error));
      if (!r.error) { input.value = r.value; return; }   // válido: lo envía Shopify
      e.preventDefault();
      show(r.error, 'error');
      if (r.fix) { input.value = r.fix; input.select(); } else input.focus();
    });
  }

  /* Contacto y cualquier formulario con data-validate: cada campo dice su regla con data-rule */
  document.querySelectorAll('form[data-validate], #contactForm').forEach(form => {
    const fields = [...form.querySelectorAll('[data-rule]')];
    const msg = form.querySelector('[data-form-msg]');
    const check = el => {
      const err = (RULES[el.dataset.rule] || (() => ''))(el.value);
      const out = form.querySelector(`[data-error-for="${el.id}"]`);
      el.setAttribute('aria-invalid', String(!!err));
      if (out) out.textContent = err;
      return err;
    };
    let tried = false;
    fields.forEach(el => el.addEventListener('blur', () => { if (tried) check(el); }));
    form.addEventListener('submit', e => {
      tried = true;
      const bad = fields.filter(el => check(el));
      if (!bad.length) {
        const email = fields.find(el => el.dataset.rule === 'email');
        if (email) email.value = checkEmail(email.value).value;
        return;
      }
      e.preventDefault();
      if (msg) {
        msg.textContent = bad.length === 1 ? 'Revisa el campo marcado.' : `Revisa los ${bad.length} campos marcados.`;
        msg.classList.add('is-error');
      }
      bad[0].focus();
    });
  });

  /* Antispam en los formularios de avisos y contacto, además del filtro de Shopify:
     un campo trampa invisible (las personas no lo ven, los bots lo llenan) y un tiempo mínimo
     de llenado. A un bot se le responde como si todo saliera bien, pero no se envía nada. */
  const bornAt = Date.now();
  const MIN_FILL_MS = 2500;
  ['newsForm', 'popNewsForm', 'contactForm'].forEach(id => {
    const form = document.getElementById(id);
    if (!form || form.dataset.antispam) return;
    form.dataset.antispam = '1';
    const trap = document.createElement('div');
    trap.className = 'hp';
    trap.setAttribute('aria-hidden', 'true');
    trap.innerHTML = '<label>No llenes este campo <input type="text" name="sitio_web" tabindex="-1" autocomplete="off"></label>';
    form.appendChild(trap);
    form.addEventListener('submit', e => {
      if (e.defaultPrevented) return;                            // la validación ya lo detuvo
      const email = form.querySelector('input[type="email"]');
      if (email && checkEmail(email.value).error) return;        // el error lo marca la validación
      const bot = form.elements.sitio_web && form.elements.sitio_web.value !== '';
      if (!bot && Date.now() - bornAt >= MIN_FILL_MS) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const out = form.querySelector('[role="status"]');
      if (out) { out.textContent = 'Listo. Gracias.'; out.classList.add('is-ok'); out.classList.remove('is-error'); }
      form.reset();
    });   // después de la validación: solo frena envíos que ya pasaron los campos
  });

  // analítica: eventos propios para los píxeles de Shopify (Configuración > Eventos de clientes)
  const publish = (name, data) => { try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {}); } catch (e) { /* sin analítica */ } };
  if (/[?&]customer_posted=true/.test(location.search)) publish('optimates_newsletter_signup', { origen: location.hash === '#popNewsForm' ? 'ventana' : 'pie' });
  const popEl = document.getElementById('newsPop');
  if (popEl && window.MutationObserver) {
    new MutationObserver(() => { if (popEl.open) publish('optimates_popup_view'); }).observe(popEl, { attributes: true, attributeFilter: ['open'] });
  }

  // botones "Copiar" (data-copy="texto")
  document.addEventListener('click', async e => {
    const b = e.target.closest('[data-copy]');
    if (!b) return;
    const label = b.textContent;
    try { await navigator.clipboard.writeText(b.dataset.copy); b.textContent = 'Copiado'; }
    catch (err) { b.textContent = 'Selecciona y copia'; }
    setTimeout(() => { b.textContent = label; }, 1800);
  });

  /* ============================================================
     Ventana de avisos del drop: sale una vez (a los N segundos o al bajar media página).
     Si la cierran no vuelve en N días; si se inscriben, no vuelve.
     ============================================================ */
  function newsPopup() {
    const pop = document.getElementById('newsPop');
    if (!pop || typeof pop.showModal !== 'function' || pop.dataset.ready) return;
    pop.dataset.ready = '1';
    const KEY = 'opt-aviso';
    const DAYS = Number(pop.dataset.days) || 14;
    const DELAY = (Number(pop.dataset.delay) || 5) * 1000;
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } };
    const save = v => { try { localStorage.setItem(KEY, JSON.stringify(Object.assign(read(), v))); } catch (e) { /* modo privado */ } };
    const form = pop.querySelector('form');
    const input = pop.querySelector('input[type="email"]');
    const msg = document.getElementById('popMsg');

    const close = () => { save({ cerrado: Date.now() }); if (pop.open) pop.close(); };
    pop.addEventListener('click', e => { if (e.target === pop || e.target.closest('[data-pop-close]')) close(); });
    pop.addEventListener('cancel', e => { e.preventDefault(); close(); });   // tecla Esc

    // el correo se valida antes de enviarlo; el envío lo hace Shopify y la página vuelve con el resultado
    form.addEventListener('submit', e => {
      const r = checkEmail(input.value);
      input.setAttribute('aria-invalid', String(!!r.error));
      if (!r.error) { input.value = r.value; return; }
      e.preventDefault();
      msg.textContent = r.error;
      msg.classList.add('is-error'); msg.classList.remove('is-ok');
      if (r.fix) { input.value = r.fix; input.select(); } else input.focus();
    });

    // de vuelta de un envío: cualquier formulario de avisos inscrito cuenta
    const posted = /[?&]customer_posted=true/.test(location.search);
    if (posted) save({ suscrito: true });
    const state = msg && msg.dataset.popState;
    if (location.hash === '#popNewsForm' && state) {
      // el pie también lee el mismo resultado: se oculta para no repetir el mensaje
      const foot = document.getElementById('newsMsg');
      if (foot) foot.textContent = '';
      pop.showModal();
      if (state === 'ok') setTimeout(() => { if (pop.open) pop.close(); }, 3200);
      return;
    }

    // enlace de prueba: ?aviso=1 la abre al momento y olvida si ya se había cerrado
    if (/[?&]aviso=1/.test(location.search)) {
      try { localStorage.removeItem(KEY); } catch (e) { /* modo privado */ }
      pop.showModal();
      return;
    }
    if (window.Shopify && window.Shopify.designMode) return;   // en el editor se abre al seleccionar la sección
    const seen = read();
    if (seen.suscrito || (seen.cerrado && Date.now() - seen.cerrado < DAYS * 864e5)) return;

    const t0 = performance.now();
    let done = false, timer = 0;
    const open = () => {
      if (done) return;
      // no se encima a la bolsa ni a otra ventana
      if (document.querySelector('.drawer.is-open, dialog[open]')) { clearTimeout(timer); timer = setTimeout(open, 4000); return; }
      done = true;
      clearTimeout(timer);
      removeEventListener('scroll', onScroll);
      pop.showModal();
    };
    const onScroll = () => {
      if (performance.now() - t0 < 2500) return;
      if (scrollY > (root.scrollHeight - innerHeight) * .33) open();
    };
    timer = setTimeout(open, DELAY);
    addEventListener('scroll', onScroll, { passive: true });
  }
  newsPopup();
  document.addEventListener('shopify:section:load', e => {
    newsPopup();
    // en el editor: al cambiar un ajuste de la ventana se vuelve a mostrar para ver el resultado
    const pop = document.getElementById('newsPop');
    if (pop && pop.dataset.sectionId === e.detail.sectionId && !pop.open) pop.showModal();
  });
  document.addEventListener('shopify:section:select', e => {
    const pop = document.getElementById('newsPop');
    if (pop && pop.dataset.sectionId === e.detail.sectionId && !pop.open) pop.showModal();
  });
  document.addEventListener('shopify:section:deselect', e => {
    const pop = document.getElementById('newsPop');
    if (pop && pop.dataset.sectionId === e.detail.sectionId && pop.open) pop.close();
  });

  /* ============================================================
     6. Cookies: el aviso es el de Shopify (Ajustes > Privacidad del cliente).
        El enlace del pie solo aparece si ese aviso está activo.
     ============================================================ */
  const cookiesBtn = document.querySelector('[data-cookies]');
  if (cookiesBtn) {
    const ready = () => window.privacyBanner && typeof privacyBanner.showPreferences === 'function';
    const reveal = () => { if (ready()) cookiesBtn.hidden = false; return ready(); };
    if (!reveal()) addEventListener('load', () => { if (!reveal()) setTimeout(reveal, 2500); });
    cookiesBtn.addEventListener('click', () => { if (ready()) privacyBanner.showPreferences(); });
  }
})();
